import { useQuery, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Doughnut, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import { statsAPI, rankingsAPI } from '../utils/api';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

export default function Dashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => statsAPI.dashboard().then(r => r.data.data)
  });

  const recalcMutation = useMutation({
    mutationFn: () => rankingsAPI.recalculate(),
    onSuccess: () => toast.success('✅ تم إعادة حساب التصنيفات بنجاح'),
    onError: () => toast.error('حدث خطأ أثناء إعادة الحساب')
  });

  if (isLoading) return <div className="loading"><div className="spinner" /></div>;
  if (!data) return null;

  const { stats, topPlayers, sportDistribution, recentParticipations, countryDistribution } = data;

  const sportChart = {
    labels: sportDistribution?.map(s => s.name_ar || s.name) || [],
    datasets: [{
      data: sportDistribution?.map(s => s.player_count) || [],
      backgroundColor: ['#00d4ff','#ffd700','#7c4dff','#00e676','#ff4444','#ff9800','#e91e63','#00bcd4','#8bc34a','#9c27b0'],
      borderWidth: 0,
    }]
  };

  const countryChart = {
    labels: countryDistribution?.map(c => c.flag_emoji + ' ' + (c.name_ar || c.name)) || [],
    datasets: [{
      label: 'عدد اللاعبين',
      data: countryDistribution?.map(c => c.player_count) || [],
      backgroundColor: 'rgba(0,212,255,0.3)',
      borderColor: '#00d4ff',
      borderWidth: 1,
    }]
  };

  return (
    <div>
      <div className="topbar">
        <div className="topbar-title">📊 لوحة التحكم</div>
        <button className="btn btn-gold" onClick={() => recalcMutation.mutate()} disabled={recalcMutation.isPending}>
          {recalcMutation.isPending ? '⏳ جاري الحساب...' : '🔄 إعادة حساب التصنيفات'}
        </button>
      </div>

      <div className="page-content">
        {/* Stats */}
        <div className="stat-grid">
          <div className="stat-card">
            <div className="stat-icon">👤</div>
            <div className="stat-value">{stats.total_players}</div>
            <div className="stat-label">إجمالي اللاعبين</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🎯</div>
            <div className="stat-value">{stats.total_tournaments}</div>
            <div className="stat-label">البطولات المسجلة</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🏅</div>
            <div className="stat-value">{stats.total_participations}</div>
            <div className="stat-label">إجمالي المشاركات</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⚽</div>
            <div className="stat-value">{stats.total_sports}</div>
            <div className="stat-label">الرياضات</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🌍</div>
            <div className="stat-value">{stats.total_countries}</div>
            <div className="stat-label">الدول</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
          {/* Top 5 players */}
          <div className="card">
            <div className="section-header"><h2>🏆 أعلى 5 لاعبين</h2></div>
            {topPlayers?.map((p, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 0', borderBottom: i < 4 ? '1px solid var(--border)' : 'none' }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: ['#ffd700','#c0c8d8','#cd7f32','var(--bg-secondary)','var(--bg-secondary)'][i], display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 13, color: i < 3 ? '#111' : 'var(--text-secondary)', border: i >= 3 ? '1px solid var(--border)' : 'none' }}>
                  {i + 1}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{p.full_name_ar || p.full_name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{p.flag_emoji} {p.icon} {p.sport_name}</div>
                </div>
                <div className="points-value">{Math.round(p.total_points).toLocaleString()}</div>
              </div>
            ))}
          </div>

          {/* Sport distribution */}
          <div className="card">
            <div className="section-header"><h2>⚽ توزيع الرياضات</h2></div>
            {sportDistribution?.length > 0 && (
              <Doughnut data={sportChart} options={{ plugins: { legend: { labels: { color: '#8fa3c5', font: { family: 'Cairo' } } } }, maintainAspectRatio: true }} />
            )}
          </div>
        </div>

        {/* Country distribution chart */}
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="section-header"><h2>🌍 اللاعبون حسب الدولة</h2></div>
          <Bar data={countryChart} options={{
            plugins: { legend: { display: false } },
            scales: {
              y: { grid: { color: '#1e2d4a' }, ticks: { color: '#8fa3c5' } },
              x: { grid: { display: false }, ticks: { color: '#8fa3c5', font: { family: 'Cairo' } } }
            }
          }} />
        </div>

        {/* Recent activity */}
        <div className="card">
          <div className="section-header"><h2>🕒 آخر المشاركات المسجلة</h2></div>
          {!recentParticipations?.length ? (
            <div className="empty"><div className="empty-icon">📋</div><p>لا توجد مشاركات</p></div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>اللاعب</th><th>البطولة</th><th>المستوى</th><th>النتيجة</th><th>التاريخ</th></tr>
                </thead>
                <tbody>
                  {recentParticipations.map((r, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 600 }}>{r.full_name_ar || r.full_name}</td>
                      <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{r.tournament_name_ar || r.tournament_name}</td>
                      <td><span className={`level-badge level-${r.level}`}>{r.level}</span></td>
                      <td><span className={`medal medal-${r.medal_type}`}>{r.medal_type}</span></td>
                      <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{new Date(r.created_at).toLocaleDateString('ar')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
